import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AtButtonDirective, AtIconComponent } from '@anothertrip/anothertrip-ds';

import { BETA_NAV_ROUTES } from './beta-nav.config';

export type DashboardNavItem = DashboardNavParent | DashboardNavLink;

export interface DashboardNavParent {
  label: string;
  children: DashboardNavLink[];
}

export interface DashboardNavLink {
  label: string;
  icon: string;
  routerLink: string;
  /**
   * Match the URL exactly rather than by prefix.
   *
   * Needed by any link whose route is an ancestor of its siblings: `/dashboard` is a prefix of
   * `/dashboard/posts`, so without this the Home item stays lit on every page in the section and
   * the nav stops answering "where am I".
   */
  exact?: boolean;
  /**
   * How many things behind this link are waiting on the creator (e.g. campaign invitations to
   * answer): a mint count at the row's end, hidden at zero. The operator console's nav count, same
   * treatment. A signal so the item list itself can stay a plain constant.
   */
  count?: Signal<number>;
  /** What the count counts, read after the number to screen readers ("2 waiting for your response"). */
  countLabel?: string;
}

@Component({
  selector: 'app-dashboard-nav-list',
  imports: [NgTemplateOutlet, AtButtonDirective, AtIconComponent, RouterLink, RouterLinkActive],
  templateUrl: './dashboard-nav-list.component.html',
  styleUrl: './dashboard-nav-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'dashboard-nav-list-host',
  },
})
export class DashboardNavListComponent {
  readonly items = input<DashboardNavItem[]>([]);

  readonly itemClick = output<MouseEvent>();

  // Where beta feedback goes until Intercom replaces it.
  protected readonly feedbackEmail = 'support@anothertrip.com';

  /**
   * Which items carry the early-access tag (AT-2264 / AT-2370 / AT-2372): the config list, never the
   * item, so flagging one is a line in `beta-nav.config.ts`. The tag is a label only.
   *
   * There was a quieter "Coming soon" tag too. Its only user, Opportunities, opens a working page, so
   * the tag said the opposite of what the link did and went (AT-2637).
   */
  private readonly betaRoutes = new Set(inject(BETA_NAV_ROUTES));

  protected isBeta(link: DashboardNavLink): boolean {
    return this.betaRoutes.has(link.routerLink);
  }

  /** One feedback line under the whole list, shown while any item on screen is in beta. */
  protected readonly hasBetaItem = computed(() =>
    this.items().some((item) =>
      'children' in item ? item.children.some((child) => this.isBeta(child)) : this.isBeta(item)
    )
  );
}
